import {useState, useEffect, useRef, useContext, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import moment from "moment";

import AuthContext from "context/AuthContext";
import NavBreadcrum from "components/NavBreadcrum";
import {ContainerSVG} from "components/waste-icons/svgs/ContainerSVG";
import navs from "../../navs";
import axiApi from "ApiCaller";
import {SwalAlertClass} from "core/swalClass";
import FormRouteIslands from "./FormRouteIslands";
import FormRouteMap from "./FormRouteMap";
import FormRouteInfo from "./FormRouteInfo";
import ButtonDeleteRoute from "./ButtonDeleteRoute";
import {RouteModel, RouteReadOnlyStatus} from "site-apps/distribution/route/RouteModel";
import {publishEvent, subscribeEvent, unsubscribeEvent} from "core/eventsConnector";
import ConditionalRender from "components/ConditionalRender";

const IconographyModel = {
    name: "",
    icon: <FontAwesomeIcon icon="question" size="3x" />,
    download: "",
};

/**
 * FormRoute component facilitates the management and editing of route data within a modal or standalone context.
 *
 * @param {string} [format="edit"] - Determines the operational mode for the route form.
 *                                       Possible values are 'edit', 'modify', or 'virtual'.
 *                                       "edit" -> When call formulary directly by url id.
 *                                       "modify" -> When modify or view route instance.
 *                                       "virtual" -> When create route in future.
 * @param {?string} [idModal=null] - The unique identifier associated with the route when opened in a modal context.
 * @param {Function} handleClose - Callback function to handle the closing of the modal or form.
 * @param {Object} virtual - Configuration for virtual data associated with the route.
 *                           {programming: `id`, route: `id`, created: `Date`}
 */
const FormRoute = ({
    format = "edit", // edit | modify | virtual
    idModal = null,
    handleClose,
    virtual = {prog: null, route: null, created: null, scheduleId: null},
}) => {
    const [_Tr] = useTranslation("routes");
    const [_Tc] = useTranslation("choices");
    const {t: _Tf} = useTranslation("forms");
    const title = _Tf("labels.route");
    const {idParams} = useParams();
    const {panel} = useContext(AuthContext);
    const Alert = new SwalAlertClass({confirmButtonText: _Tf("labels.accept")});
    const [formData, setFormData] = useState({...RouteModel});
    const bread = useMemo(
        () => [
            {
                name: _Tr("labels.route-management"),
                url: "/route-management/",
                current: false,
            },
            {
                name: _Tr("labels.calendar"),
                url: "/route-management/calendar/",
                current: false,
            },
            {name: _Tf("labels.route"), current: true},
        ],
        [_Tr, _Tf],
    );
    const [iconograph, setIconograph] = useState({...IconographyModel});
    const [waypoints, setWaypoints] = useState([]);
    const dataChanged = useRef({waypoints: [], geojson: {}});

    const [geojson, setGeojson] = useState({});
    const [hasChangedPoints, setHasChangedPoints] = useState(false);
    const [highlighted, setHighligthed] = useState(null);
    const styleList = useRef({
        maxWidth: "500px",
        height: "calc(100vh - 320px)",
        overflow: "hidden",
        overflowY: "scroll",
    });
    const styleMap = useRef({height: "calc(100vh - 320px)", width: "100%"});
    const [inModal, setInModal] = useState(false);
    const [canSave, setCanSave] = useState(false);

    useEffect(() => {
        let dataSend = {};
        switch (format) {
            case "edit":
                dataSend = {id: idParams, type: format};
                setInModal(false);
                document.title = title;
                break;
            case "modify":
                dataSend = {id: idModal, type: format};
                setInModal(true);
                break;
            case "virtual":
                dataSend = {
                    programming: virtual.prog,
                    route: virtual.route,
                    created: virtual.created,
                    type: format,
                };
                setInModal(true);
                break;
            default:
        }
        fetchData(dataSend);
    }, []);

    const fetchData = (dataSend) => {
        axiApi
            .post("/api/distribution/route/", dataSend)
            .then((resp) => {
                if (resp.data.status) {
                    const formData_ = {...resp.data.item};
                    const icon = {...iconograph};
                    icon.name = _Tc("wastes." + formData_.waste_type);
                    icon.icon = <ContainerSVG type={formData_.waste_type} />;
                    icon.download = _Tc("downloads." + formData_.download_type);
                    setIconograph({...icon});
                    setWaypoints([...formData_.checkpoints]);
                    dataChanged.current.waypoints = [...formData_.checkpoints];
                    setGeojson(formData_.geojson);
                    dataChanged.current.geojson = formData_.geojson;
                    formData_.created_at = moment(formData_.created_at);
                    if (
                        formData_.created_at.isSameOrAfter(moment(), "day") &&
                        !RouteReadOnlyStatus.includes(formData_.unload_status)
                    ) {
                        setCanSave(true);
                    }
                    setFormData({...formData_});
                    if (!formData_.geojson || !Object.keys(formData_.geojson).length) {
                        setHasChangedPoints(true);
                    }
                } else {
                    Alert.error(_Tf("errors.load-error"), _Tf("errors.route-error"));
                }
            })
            .catch((err) => {
                console.error(err);
                Alert.error(_Tf("errors.load-error"), _Tf("errors.route-error"));
            });
    };

    useEffect(() => {
        if (format !== "edit" || !idParams) return;

        const onRouteSync = (e) => {
            if (e.detail.route_id == idParams) {
                fetchData({id: idParams, type: "edit"});
            }
        };

        subscribeEvent("app:route_sync", onRouteSync);
        return () => {
            unsubscribeEvent("app:route_sync", onRouteSync);
        };
    }, [idParams]);

    const onSubmitVirtualData = () => {
        let dataSend = {
            programming: formData.prog_id,
            route: formData.route_id,
            created: formData.created_at.toISOString(),
            duration: parseInt(formData.duration),
            waypoints: [],
            geojson: dataChanged.current.geojson,
            driver_id: formData.driver?.id ?? null,
            truck_id: formData.truck?.id ?? null,
        };
        dataChanged.current.waypoints.forEach((waypoint) => {
            let active = {
                id: "",
                is_asset: waypoint.is_asset,
                order: waypoint.order,
                step: waypoint.step ?? 0,
            };

            if (active.is_asset) {
                if (waypoint.island_id || waypoint.is_island) {
                    active.island_id = waypoint.island_id;
                    active.asset_ids = waypoint.asset_ids || (waypoint.assets || []).map(a => a.asset_id).filter(id => id);
                } else {
                    active.asset_id = waypoint.asset_id;
                }
            } else {
                active.waypoint_id = waypoint.waypoint_id;
            }
            dataSend.waypoints.push(active);
        });
        axiApi
            .put(`/api/distribution/route/`, dataSend)
            .then((resp) => {
                if (resp.data.status) {
                    let formData_ = {...resp.data.item};
                    formData_.created_at = moment(formData_.created_at);
                    setFormData({...formData_});
                    setWaypoints([...resp.data.item.checkpoints]);
                    setGeojson(resp.data.item.geojson);
                    dataChanged.current.geojson = resp.data.item.geojson;
                    Alert.success(_Tf("success.updated-route"), _Tf("success.updated-route-description"));
                    publishEvent("route:modalform:change", formData_);
                    handleClose();
                } else {
                    Alert.error("Error al actualizar", resp.data.message);
                }
            })
            .catch((err) => {
                console.error(err);
            });
    };

    const onClickCalculate = async () => {
        try {
            const coords = waypoints.map((point) => [point.lng, point.lat]);
            const resp = await axiApi.post("/api/distribution/route/travel/", {coordinates: coords, optimize: false});

            let times = 0;
            const unload = (panel.unloading || 0) * 60;
            const items = [waypoints[0]];
            resp.data.geojson.legs.forEach((leg, idx) => {
                const item_ = {...waypoints[idx + 1]};
                if (item_.is_asset) {
                    times += unload;
                    item_.step = leg.duration + unload;
                } else {
                    times += (item_.waypoint_time || 0) * 60;
                    item_.step = leg.duration + (item_.waypoint_time || 0) * 60;
                }
                times += leg.duration;
                items.push(item_);
            });

            setWaypoints(items);
            dataChanged.current.waypoints = items;
            dataChanged.current.geojson = resp.data.geojson;
            setGeojson(resp.data.geojson);
            setFormData((prev) => ({...prev, duration: Math.ceil(times)}));
            setHasChangedPoints(false);
        } catch (error) {
            console.error("Error al calcular la ruta:", error);
        }
    };

    const onSummit = (e) => {
        if (hasChangedPoints) {
            Alert.warning("Ruta modificada", "Por favor calcule la nueva ruta.");
            return false;
        }
        if (format === "virtual") {
            onSubmitVirtualData();
            return false;
        }
        let dataSend = {
            id: formData.id,
            waypoints: [],
            geojson: dataChanged.current.geojson,
            driver_id: formData.driver?.id,
            truck_id: formData.truck?.id ?? null,
            requires: formData.requires || {},
        };
        dataChanged.current.waypoints.forEach((waypoint) => {
            const active = {
                id: waypoint.id,
                is_asset: waypoint.is_asset,
                mooch: waypoint.mooch,
                order: waypoint.order,
            };

            if (waypoint.is_island) {
                active.island_id = waypoint.island_id;
                active.asset_ids = waypoint.asset_ids || [];
            } else if (active.is_asset) {
                active.asset_id = waypoint.asset_id;
            } else if (waypoint.mooch) {
                active.asset_id = waypoint.island_id;
            } else {
                active.waypoint_id = waypoint.waypoint_id;
            }
            dataSend.waypoints.push(active);
        });

        axiApi
            .patch(`/api/distribution/route/`, dataSend)
            .then((resp) => {
                if (resp.data.status) {
                    const formData_ = {...resp.data.item};
                    formData_.created_at = moment(formData_.created_at);
                    setFormData({...formData_});
                    setWaypoints([...resp.data.item.checkpoints]);
                    setGeojson(resp.data.item.geojson);
                    dataChanged.current.geojson = resp.data.item.geojson;
                    if (inModal) {
                        publishEvent("route:modalform:change", formData_);
                        handleClose();
                    }
                    Alert.success(_Tf("success.updated-route"), _Tf("success.updated-route-description"));
                } else {
                    Alert.error(_Tf("errors.update-error"), resp.data.message);
                }
            })
            .catch((err) => {
                console.error(err);
            });
    };
    const IconsForm = {
        s: <FontAwesomeIcon icon="location-dot" />,
        d: <FontAwesomeIcon icon="route" />,
        f: <FontAwesomeIcon icon="sack-xmark" />,
    };

    return (
        <>
            <ConditionalRender condition={!inModal}>
                <NavBreadcrum navs={navs} bread={bread} />
            </ConditionalRender>
            <div className="col-12 preview-canvas offcanvas-header mt-1">
                <div className="preview-icon">{IconsForm[formData.type]}</div>
                <div className="preview-title ps-4">
                    <h5>
                        {_Tf("labels.route")}: <strong>{formData.name}</strong>
                    </h5>
                    <h6>
                        Prog: <strong>{formData.prog_name}</strong>
                    </h6>
                </div>
                <div className="ms-auto text-secondary pe-3">
                    <div className="text-center">{iconograph.name}</div>
                    <div className="text-center">{iconograph.icon}</div>
                    <div className="text-center">{iconograph.download}</div>
                </div>
            </div>
            <div className="row">
                <div className="col-12">
                    <FormRouteInfo
                        formData={formData}
                        waypoints={waypoints}
                        hasChanged={hasChangedPoints}
                        onClickCalculate={onClickCalculate}
                        onChangeDriver={(driverId) => {
                            const driver = formData.available_drivers?.find((d) => d.id === driverId) ?? null;
                            setFormData((prev) => ({...prev, driver}));
                        }}
                        onChangeTruck={(truckId) => {
                            const truck = formData.available_trucks?.find((t) => t.id === truckId) ?? null;
                            setFormData((prev) => ({...prev, truck}));
                        }}
                        requiresReadOnly={format === "virtual" || !!formData.opened_at}
                        readOnly={!canSave}
                        onChangeRequires={(key, value) => {
                            setFormData((prev) => ({...prev, requires: {...(prev.requires || {}), [key]: value}}));
                        }}
                    />
                </div>
                <div className="col" style={styleList.current}>
                    <FormRouteIslands
                        dataChanged={dataChanged}
                        waypoints={waypoints}
                        setWaypoints={setWaypoints}
                        formData={formData}
                        setFormData={setFormData}
                        hasChanged={hasChangedPoints}
                        setHasChanged={setHasChangedPoints}
                        highlighted={highlighted}
                        setHighligthed={setHighligthed}
                        canSave={canSave}
                    />
                </div>
                <div className="col">
                    <FormRouteMap
                        center={panel.coords}
                        waypoints={waypoints}
                        geojson={geojson}
                        highlightedPoint={highlighted}
                        styleMap={styleMap.current}
                        wasteType={formData.waste_type}
                        duration={formData.duration}
                    />
                </div>
            </div>
            <div className="col-12 text-end pt-2 pe-4">
                {format !== "virtual" && (
                    <ButtonDeleteRoute route={formData} onDeleted={inModal ? handleClose : null} />
                )}
                {inModal && (
                    <button className="btn btn-primary me-3" onClick={handleClose}>
                        {_Tf("labels.close")}
                    </button>
                )}
                {canSave && (
                    <button className="btn btn-primary" onClick={onSummit} disabled={hasChangedPoints}>
                        {_Tf("labels.save")}
                    </button>
                )}
            </div>
        </>
    );
};

export default FormRoute;
